import { Observable, Subject, Subscription, BehaviorSubject } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';

import { pollReset, poll } from '@sg/utils';

const DEFAULT_POLL_INTERVAL = 30000;

export interface SgPollersList {}

export interface SgIncomingPollerData<T extends keyof SgPollersList> {
  name: T;
  data: SgPollersList[T];
}

@Injectable()
export class SgPoller {
  private resetPoll$ = new Subject<keyof SgPollersList>();
  private pollers = new Map<
    keyof SgPollersList,
    { obs: Observable<any>; sub: BehaviorSubject<any>; subs?: Subscription }
  >();

  constructor() {}

  onIncoming<T extends keyof SgPollersList>(name: T): Observable<SgPollersList[T]> {
    const poller = this.pollers.get(name);
    if (!poller) {
      throw new Error(`Poller ${name} is not registered`);
    }
    if (!this.isRunning(name)) {
      this.start(name);
    }
    return poller.sub.asObservable();
  }

  reset<T extends keyof SgPollersList>(name: T): void {
    this.resetPoll$.next(name);
  }

  has<T extends keyof SgPollersList>(name: T): boolean {
    return this.pollers.has(name);
  }

  add<T extends keyof SgPollersList>(name: T, obs$: Observable<SgPollersList[T]>, interval?: number): void {
    if (this.pollers.has(name)) {
      throw new Error(`Poller ${name} is already registered`);
    }
    const sub = new BehaviorSubject<SgPollersList[T]>(undefined);
    const resetPollTrigger$ = this.resetPoll$.pipe(filter((p) => p === name));
    const obs = obs$.pipe(
      tap((data) => sub.next(data)),
      pollReset(resetPollTrigger$, interval || DEFAULT_POLL_INTERVAL)
    );
    this.pollers.set(name, { obs, sub });
  }

  remove<T extends keyof SgPollersList>(name: T): void {
    if (this.has(name)) {
      this.stop(name);
      const poller = this.pollers.get(name);
      if (!poller.sub.closed) {
        poller.sub.unsubscribe();
      }
      this.pollers.delete(name);
    }
  }

  addStartListen<T extends keyof SgPollersList>(
    name: T,
    obs$: Observable<SgPollersList[T]>,
    interval?: number
  ): Observable<SgPollersList[T]> {
    this.add(name, obs$, interval);
    this.start(name);
    return this.onIncoming(name);
  }

  start<T extends keyof SgPollersList>(name: T): void {
    const poller = this.pollers.get(name);
    if (!poller) {
      throw new Error(`Poller ${name} is not registered`);
    }
    if (!poller.subs || poller.subs.closed) {
      poller.subs = poller.obs.subscribe();
    }
  }

  stop<T extends keyof SgPollersList>(name: T): void {
    const poller = this.pollers.get(name);
    if (poller) {
      if (poller.subs && !poller.subs.closed) {
        poller.subs.unsubscribe();
      }
      poller.subs = undefined;
    }
  }

  startAll(): void {
    for (const name of Array.from(this.pollers.keys())) {
      this.start(name);
    }
  }

  stopAll(): void {
    for (const name of Array.from(this.pollers.keys())) {
      this.stop(name);
    }
  }

  isRunning<T extends keyof SgPollersList>(name: T): boolean {
    const poller = this.pollers.get(name);
    return poller && poller.subs && !poller.subs.closed;
  }
}
